/**
 * Same Old Threads - click-only desktop mega menu patch for Shopify Horizon.
 *
 * IMPORTANT:
 * This file intentionally does NOT define or replace the <header-menu>
 * custom element. Horizon may register that element before a custom asset
 * executes on the live storefront. Instead, this script controls the rendered
 * menu DOM directly, so it works regardless of custom-element registration
 * order.
 *
 * Expected markup comes from _header-menu-click-v4.liquid:
 * - parent menu items use <button data-header-menu-toggle>
 * - leaf items remain normal <a> links
 * - hover activation bindings are removed from the desktop menu items
 */

(() => {
  if (window.__SOT_CLICK_MENU_V4__) return;
  window.__SOT_CLICK_MENU_V4__ = true;

  const TOGGLE_SELECTOR = '[data-header-menu-toggle]';
  const ITEM_SELECTOR = '.menu-list__list-item';
  const MENU_SELECTOR = 'header-menu';

  /** @param {HTMLElement} item */
  function getMenuItem(item) {
    const element = item.querySelector('[ref="menuitem"]');
    return element instanceof HTMLElement ? element : null;
  }

  /** @param {HTMLElement} item */
  function getDisclosure(item) {
    const element = item.querySelector('[ref="disclosure[]"]');
    return element instanceof HTMLElement ? element : null;
  }

  /** @param {HTMLElement} item */
  function getSubmenu(item) {
    for (const child of item.children) {
      if (child instanceof HTMLElement && child.matches('[ref="submenu[]"]')) {
        return child;
      }
    }
    return null;
  }

  /** @param {HTMLElement} item @param {boolean} expanded */
  function setItemExpanded(item, expanded) {
    const menuItem = getMenuItem(item);
    const disclosure = getDisclosure(item);
    const submenu = getSubmenu(item);
    const value = expanded ? 'true' : 'false';

    menuItem?.setAttribute('aria-expanded', value);
    disclosure?.setAttribute('aria-expanded', value);

    if (submenu) {
      submenu.inert = !expanded;

      if (expanded) {
        submenu.dataset.active = '';
      } else {
        delete submenu.dataset.active;
      }
    }
  }

  /** @param {HTMLElement} headerMenu */
  function getHeaderComponent(headerMenu) {
    const component = headerMenu.closest('header-component');
    return component instanceof HTMLElement ? component : null;
  }

  /** @param {HTMLElement} headerComponent */
  function getHeaderVisibleHeight(headerComponent) {
    const overlap = headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    if (overlap && headerComponent.offsetHeight > 0) {
      const topRow = headerComponent.querySelector('.header__row--top');
      if (topRow instanceof HTMLElement) return topRow.offsetHeight;
    }

    return headerComponent.offsetHeight;
  }

  /** @param {HTMLElement} headerMenu */
  function getOverflowHost(headerMenu) {
    const host = headerMenu.querySelector('[ref="overflowMenu"]');
    return host instanceof HTMLElement ? host : null;
  }

  /** @param {HTMLElement} headerMenu */
  function getOverflowPanel(headerMenu) {
    const host = getOverflowHost(headerMenu);
    const panel = host?.shadowRoot?.querySelector('[part="overflow"]');
    return panel instanceof HTMLElement ? panel : null;
  }

  /** @param {HTMLElement} headerMenu */
  function getFirstOverflowItem(headerMenu) {
    const host = getOverflowHost(headerMenu);
    const item = host?.querySelector('[slot="overflow"]');
    return item instanceof HTMLElement ? item : null;
  }

  /** @param {HTMLElement} headerMenu */
  function getExpandedTopLevelItem(headerMenu) {
    const items = headerMenu.querySelectorAll(ITEM_SELECTOR);

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;
      if (item.slot === 'overflow') continue;

      const menuItem = getMenuItem(item);
      if (menuItem?.getAttribute('aria-expanded') === 'true') return item;
    }

    return null;
  }

  /** @param {HTMLElement} headerMenu */
  function updateOpenHeight(headerMenu) {
    const headerComponent = getHeaderComponent(headerMenu);
    if (!headerComponent) return;

    const activeItem = getExpandedTopLevelItem(headerMenu);

    if (!activeItem) {
      headerComponent.style.setProperty('--submenu-height', '0px');
      headerComponent.style.setProperty('--full-open-header-height', '0px');
      headerMenu.style.setProperty('--submenu-opacity', '0');
      return;
    }

    let submenuHeight = 0;

    if (activeItem.slot === 'more') {
      const overflowPanel = getOverflowPanel(headerMenu);
      submenuHeight = overflowPanel?.offsetHeight || overflowPanel?.scrollHeight || 0;
    } else {
      const submenu = getSubmenu(activeItem);
      submenuHeight = submenu?.offsetHeight || submenu?.scrollHeight || 0;
    }

    const headerVisibleHeight = getHeaderVisibleHeight(headerComponent);
    const fullOpenHeight = submenuHeight > 0 ? submenuHeight + headerVisibleHeight : 0;

    headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
    headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeight}px`);
    headerMenu.style.setProperty('--submenu-opacity', '1');
  }

  /** @param {HTMLElement} headerMenu */
  function scheduleHeightUpdate(headerMenu) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => updateOpenHeight(headerMenu));
    });
  }

  /** @param {HTMLElement} headerMenu */
  function closeMenu(headerMenu) {
    const items = headerMenu.querySelectorAll(ITEM_SELECTOR);

    for (const item of items) {
      if (item instanceof HTMLElement) setItemExpanded(item, false);
    }

    headerMenu.setAttribute('aria-expanded', 'false');
    headerMenu.dataset.overflowExpanded = 'false';
    headerMenu.style.setProperty('--submenu-opacity', '0');

    const headerComponent = getHeaderComponent(headerMenu);
    headerComponent?.style.setProperty('--submenu-height', '0px');
    headerComponent?.style.setProperty('--full-open-header-height', '0px');
  }

  /** @param {HTMLElement} headerMenu @param {HTMLElement} item */
  function openTopLevelItem(headerMenu, item) {
    const wasOpen = getMenuItem(item)?.getAttribute('aria-expanded') === 'true';

    closeMenu(headerMenu);

    // Clicking the currently-open parent is a true toggle: close it.
    if (wasOpen) return;

    setItemExpanded(item, true);
    headerMenu.setAttribute('aria-expanded', 'true');

    if (item.slot === 'more') {
      headerMenu.dataset.overflowExpanded = 'true';

      // Horizon reveals the overflow panel when at least one slotted overflow
      // item is expanded. Expand the first one when More opens.
      const firstOverflowItem = getFirstOverflowItem(headerMenu);
      if (firstOverflowItem) setItemExpanded(firstOverflowItem, true);
    } else {
      headerMenu.dataset.overflowExpanded = 'false';
    }

    // Preserve Horizon header coordination behavior without importing theme JS.
    headerMenu.dispatchEvent(new Event('megaMenu:hover', { bubbles: true }));
    scheduleHeightUpdate(headerMenu);
  }

  /** @param {HTMLElement} headerMenu @param {HTMLElement} item */
  function openOverflowItem(headerMenu, item) {
    const moreItem = headerMenu.querySelector(`${ITEM_SELECTOR}[slot="more"]`);

    if (moreItem instanceof HTMLElement) {
      // Ensure the More panel remains open.
      setItemExpanded(moreItem, true);
    }

    headerMenu.setAttribute('aria-expanded', 'true');
    headerMenu.dataset.overflowExpanded = 'true';

    const overflowItems = headerMenu.querySelectorAll(`${ITEM_SELECTOR}[slot="overflow"]`);
    for (const overflowItem of overflowItems) {
      if (overflowItem instanceof HTMLElement) setItemExpanded(overflowItem, overflowItem === item);
    }

    scheduleHeightUpdate(headerMenu);
  }

  document.addEventListener('click', (event) => {
    const path = event.composedPath();
    const trigger = path.find(
      (node) => node instanceof HTMLElement && node.matches(TOGGLE_SELECTOR)
    );

    if (!(trigger instanceof HTMLElement)) return;

    const headerMenu = trigger.closest(MENU_SELECTOR);
    const item = trigger.closest(ITEM_SELECTOR);

    if (!(headerMenu instanceof HTMLElement) || !(item instanceof HTMLElement)) return;

    event.preventDefault();

    if (item.slot === 'overflow') {
      openOverflowItem(headerMenu, item);
    } else {
      openTopLevelItem(headerMenu, item);
    }
  });

  // Close when the customer clicks/taps anywhere outside the desktop menu.
  document.addEventListener('pointerdown', (event) => {
    document.querySelectorAll(MENU_SELECTOR).forEach((menu) => {
      if (!(menu instanceof HTMLElement)) return;
      if (menu.getAttribute('aria-expanded') !== 'true') return;
      if (event.composedPath().includes(menu)) return;
      closeMenu(menu);
    });
  });

  // Escape closes the menu and returns focus to the open top-level control.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll(MENU_SELECTOR).forEach((menu) => {
      if (!(menu instanceof HTMLElement)) return;
      if (menu.getAttribute('aria-expanded') !== 'true') return;

      const activeItem = getExpandedTopLevelItem(menu);
      const activeControl = activeItem ? getMenuItem(activeItem) : null;

      closeMenu(menu);
      activeControl?.focus();
    });
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll(MENU_SELECTOR).forEach((menu) => {
      if (menu instanceof HTMLElement && menu.getAttribute('aria-expanded') === 'true') {
        scheduleHeightUpdate(menu);
      }
    });
  });
})();